<template>
  <ModalComponent
    submitButtonText=""
    :hideSubmitButton="true"
    @setForceToggle="setForceToggle"
    @close="handleClose"
    size="lg"
  >
    <template #title>
      {{ $t("production_strategy.bulk_issuance.title") }}
    </template>

    <template #body>
      <div class="p-3">
        <template v-if="modalState === 'initial'">
          <p class="mb-3">
            {{ $t("production_strategy.bulk_issuance.description") }}
          </p>

          <div class="mb-3">
            <label class="form-label fw-bold">{{
              $t("production_strategy.bulk_issuance.production_period")
            }}</label>
            <div class="d-flex align-items-center gap-2">
              <div class="d-flex gap-1">
                <select
                  v-model="periodFromYear"
                  class="form-select period-select-year"
                >
                  <option value="">{{ $t("common.year") }}</option>
                  <option v-for="year in yearOptions" :key="year" :value="year">
                    {{ year }}
                  </option>
                </select>
                <select
                  v-model="periodFromMonth"
                  class="form-select period-select-month"
                  :disabled="!periodFromYear"
                >
                  <option value="">{{ $t("common.month") }}</option>
                  <option
                    v-for="month in monthOptions"
                    :key="month.value"
                    :value="month.value"
                  >
                    {{ month.label }}
                  </option>
                </select>
              </div>
              <span>&mdash;</span>
              <div class="d-flex gap-1">
                <select
                  v-model="periodToYear"
                  class="form-select period-select-year"
                >
                  <option value="">{{ $t("common.year") }}</option>
                  <option v-for="year in yearOptions" :key="year" :value="year">
                    {{ year }}
                  </option>
                </select>
                <select
                  v-model="periodToMonth"
                  class="form-select period-select-month"
                  :disabled="!periodToYear"
                >
                  <option value="">{{ $t("common.month") }}</option>
                  <option
                    v-for="month in monthOptions"
                    :key="month.value"
                    :value="month.value"
                  >
                    {{ month.label }}
                  </option>
                </select>
              </div>
            </div>
            <small
              v-if="periodFrom && periodTo && periodFrom > periodTo"
              class="text-danger"
            >
              {{ $t("production_strategy.bulk_issuance.period_error") }}
            </small>
          </div>

          <div class="mb-2 d-flex align-items-center justify-content-between">
            <label class="form-label fw-bold mb-0">{{
              $t("production_strategy.bulk_issuance.companies_label")
            }}</label>
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                :checked="
                  companies.length > 0 &&
                  selectedCompanies.size === companies.length
                "
                :indeterminate.prop="
                  selectedCompanies.size > 0 &&
                  selectedCompanies.size < companies.length
                "
                @change="
                  toggleAllCompanies(
                    ($event.target as HTMLInputElement).checked
                  )
                "
              />
              <label class="form-check-label small">{{
                $t("common.select_all")
              }}</label>
            </div>
          </div>
        </template>

        <div v-if="errorMessage" class="alert alert-danger mb-3">
          {{ errorMessage }}
        </div>

        <div class="company-list">
          <div
            v-for="company in companyStatuses"
            :key="company.companyId"
            class="company-item d-flex align-items-center justify-content-between py-2 border-bottom"
          >
            <div class="d-flex align-items-center">
              <template v-if="modalState === 'initial'">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  :checked="selectedCompanies.has(company.companyId)"
                  @change="toggleCompany(company.companyId)"
                />
              </template>
              <template v-else>
                <FontAwesomeIcon icon="building" class="me-2 text-muted" />
              </template>
              <span>{{ company.companyId }}</span>
            </div>
            <div
              v-if="modalState !== 'initial'"
              class="status-indicator d-flex align-items-center"
            >
              <CompanyStatusIcon :status="company.status" />
              <span class="ms-2 small text-muted">{{
                getStatusText(company)
              }}</span>
            </div>
          </div>
        </div>

        <div
          v-if="companies.length === 0 && modalState === 'initial'"
          class="text-muted text-center py-3"
        >
          {{ $t("production_strategy.bulk_issuance.no_companies") }}
        </div>
      </div>
    </template>

    <template #footer>
      <template v-if="modalState === 'initial'">
        <button
          type="button"
          class="btn btn-link text-secondary"
          @click="handleClose"
        >
          {{ $t("common.cancel") }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="isConfirmDisabled"
          @click="showStartConfirmation = true"
        >
          {{ $t("production_strategy.bulk_issuance.confirm_all") }}
        </button>
      </template>

      <template v-else-if="modalState === 'processing'">
        <button
          type="button"
          class="btn btn-link text-secondary"
          @click="handleCancelClick"
        >
          {{ $t("common.cancel") }}
        </button>
        <button type="button" class="btn btn-secondary" disabled>
          <FontAwesomeIcon icon="spinner" class="fa-spin me-1" />
          {{ $t("production_strategy.bulk_issuance.in_progress") }}
        </button>
      </template>

      <template v-else>
        <button type="button" class="btn btn-secondary" disabled>
          {{ $t("common.cancel") }}
        </button>
        <button type="button" class="btn btn-primary" @click="handleClose">
          {{ $t("common.close") }}
        </button>
      </template>
    </template>
  </ModalComponent>

  <ConfirmationModalComponent
    :visible="showStartConfirmation"
    :title="$t('production_strategy.bulk_issuance.confirm_title')"
    :message="confirmMessage"
    :preserveWhitespace="true"
    @confirm="handleConfirmStart"
    @cancel="showStartConfirmation = false"
  />

  <ConfirmationModalComponent
    :visible="showCancelConfirmation"
    :title="$t('production_strategy.bulk_issuance.cancel_title')"
    :message="$t('production_strategy.bulk_issuance.cancel_message')"
    confirmButtonClass="btn-warning"
    @confirm="handleConfirmCancel"
    @cancel="showCancelConfirmation = false"
  >
    <div v-if="processedCompanies.length" class="mt-3">
      <p class="mb-2 fw-bold">
        {{ $t("production_strategy.bulk_issuance.already_processed") }}
      </p>
      <ul class="list-unstyled mb-0">
        <li v-for="company in processedCompanies" :key="company">
          {{ company }}
        </li>
      </ul>
    </div>
  </ConfirmationModalComponent>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import ModalComponent from "@/components/common/ModalComponent.vue";
import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import CompanyStatusIcon from "./CompanyStatusIcon.vue";
import {
  useBulkIssuance,
  type CompanyProgress,
} from "../composables/useBulkIssuance";

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();

const {
  modalState,
  companies,
  selectedCompanies,
  periodFrom,
  periodTo,
  companyStatuses,
  processedCompanies,
  isConfirmDisabled,
  errorMessage,
  fetchCompanies,
  startIssuance,
  cancelIssuance,
  toggleCompany,
  toggleAllCompanies,
  reset,
} = useBulkIssuance();

const currentYear = new Date().getFullYear();
const yearOptions = [currentYear - 1, currentYear];
const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1).padStart(2, "0"),
  label: String(i + 1).padStart(2, "0"),
}));

function useYearMonthParts(source: typeof periodFrom, defaultMonth: string) {
  const year = computed({
    get: () => (source.value ? source.value.slice(0, 4) : ""),
    set: (newYear: string) => {
      if (!newYear) {
        source.value = "";
        return;
      }
      const currentMonth = source.value
        ? source.value.slice(5, 7)
        : defaultMonth;
      source.value = `${newYear}-${currentMonth}`;
    },
  });

  const month = computed({
    get: () => (source.value ? source.value.slice(5, 7) : ""),
    set: (newMonth: string) => {
      if (!newMonth || !year.value) return;
      source.value = `${year.value}-${newMonth}`;
    },
  });

  return { year, month };
}

const { year: periodFromYear, month: periodFromMonth } = useYearMonthParts(
  periodFrom,
  "01"
);
const { year: periodToYear, month: periodToMonth } = useYearMonthParts(
  periodTo,
  "12"
);

const confirmMessage = computed(() => {
  const periodPart =
    periodFrom.value || periodTo.value
      ? `\n${t("production_strategy.bulk_issuance.production_period")}: ${periodFrom.value || "..."} — ${periodTo.value || "..."}`
      : "";
  return (
    t("production_strategy.bulk_issuance.confirm_message", {
      count: selectedCompanies.value.size,
    }) + periodPart
  );
});

const forceToggle = ref<((value: boolean) => void) | null>(null);
const showStartConfirmation = ref(false);
const showCancelConfirmation = ref(false);

const setForceToggle = (fn: (value: boolean) => void) => {
  forceToggle.value = fn;
  fn(props.visible);
};

watch(
  () => props.visible,
  async (value) => {
    forceToggle.value?.(value);
    if (value) {
      reset();
      await fetchCompanies();
    }
  }
);

const handleClose = () => {
  forceToggle.value?.(false);
  emit("close");
};

const handleConfirmStart = async () => {
  showStartConfirmation.value = false;
  await startIssuance();
};

const handleCancelClick = () => {
  showCancelConfirmation.value = true;
};

const handleConfirmCancel = async () => {
  showCancelConfirmation.value = false;
  await cancelIssuance();
};

const getStatusText = (company: CompanyProgress): string => {
  switch (company.status) {
    case "pending":
      return t("production_strategy.bulk_issuance.status.pending");
    case "in_progress":
      return t("production_strategy.bulk_issuance.status.in_progress");
    case "completed":
      return t("production_strategy.bulk_issuance.status.completed", {
        count: company.issuedCount ?? 0,
      });
    case "skipped":
      return t("production_strategy.bulk_issuance.status.skipped");
    case "error":
      return (
        company.message ?? t("production_strategy.bulk_issuance.status.error")
      );
    default:
      return "";
  }
};
</script>

<style scoped lang="scss">
.company-list {
  max-height: 400px;
  overflow-y: auto;
}

.period-select-year {
  width: 100px;
}

.period-select-month {
  width: 95px;
}
</style>
